import AppButton from '@nxsys/components/AppButton';
import { PensionSelfCertificationEnum } from '@nxsys/constants/AppEnums';
import { Radio } from 'antd';
import React from 'react';

// NX-5231: DWP money-purchase self-certification. The warning and the three
// "set" definitions are quoted from the DWP guidance "Certifying money
// purchase pension schemes". Rendered inline inside the "Advanced" expander on
// the pension settings tab (rarely used, so tucked away).
const DWP_GUIDANCE_URL =
  'https://assets.publishing.service.gov.uk/government/uploads/system/uploads/attachment_data/file/603588/money-purchase-schemes-guidance.pdf';

type SetDefinition = {
  value: PensionSelfCertificationEnum;
  title: string;
  bullets: string[];
};

// The DWP text says "PAPDIS or our API"; we name the actual pension provider
// so it is concrete for the user (e.g. "PAPDIS or NOW Pension") instead of the
// vague "our API". No "API" suffix — not every provider offers one.
const getSelfCertSets = (providerLabel: string): SetDefinition[] => [
  {
    value: PensionSelfCertificationEnum.SET_1,
    title: 'Self-certification set 1',
    bullets: [
      'Calculate contribution amounts on basic pay that includes at least salary or wages, holiday pay and any statutory payments, for example statutory maternity pay (SMP), statutory paternity pay (SPP) or statutory sick pay (SSP).',
      'You can exclude certain components of pay such as bonuses, overtime, commission and certain allowances from your calculations.',
      `You will need to use PAPDIS or ${providerLabel} for your contribution uploads.`,
      'The total minimum contribution from employer and employee must be 9%, with at least 4% from the employer.',
    ],
  },
  {
    value: PensionSelfCertificationEnum.SET_2,
    title: 'Self-certification set 2',
    bullets: [
      "Calculate contribution amounts on at least 85% of the employee's total earnings.",
      `You will need to use PAPDIS or ${providerLabel} for your contribution uploads.`,
      'The total minimum contribution from employer and employee must be 8%, with at least 3% from the employer.',
    ],
  },
  {
    value: PensionSelfCertificationEnum.SET_3,
    title: 'Self-certification set 3',
    bullets: [
      'Calculate contribution amounts on total earnings.',
      'The total minimum contribution from employer and employee must be 7%, with at least 3% from the employer.',
    ],
  },
];

const WarningIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    className="mt-[1px] flex-none"
  >
    <path d="M10 2 19 18H1L10 2Z" fill="#F79009" />
    <path d="M10 8v4.2" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="10" cy="14.7" r="1" fill="#fff" />
  </svg>
);

type SelfCertificationPanelProps = {
  value?: string;
  providerName?: string;
  // Read-only when the provider's API owns the contribution rates (Smart
  // Pension outside manual mode): the set drives those rates, so it must not
  // be changeable where the employer cannot change the rates either.
  disabled?: boolean;
  onChange: (value: PensionSelfCertificationEnum) => void;
  // NX-5231 feedback: a radio group cannot be un-ticked, so an accidental
  // click would otherwise be permanent — certifying a scheme the employer
  // never meant to certify. "Clear" puts the company back to uncertified,
  // where the pre-NX-5231 floors (8% total / 3% employer) apply.
  onClear?: () => void;
};

const SelfCertificationPanel: React.FC<SelfCertificationPanelProps> = ({
  value,
  providerName,
  disabled,
  onChange,
  onClear,
}) => {
  // Fully controlled by the form field. Nothing is certified until the
  // employer picks a set, so a blank value shows no radio ticked — Set 2 is
  // the statutory baseline but it is never presented as an existing choice.
  const providerLabel = providerName || 'your pension provider';
  const sets = getSelfCertSets(providerLabel);

  return (
    <div>
      <div className="flex items-start gap-[10px] rounded-[8px] border border-solid border-[#EAECF0] bg-white p-[16px] mb-[20px]">
        <WarningIcon />
        <div className="text-[14px] leading-[1.5] text-[#344054]">
          <div className="mb-[6px]">
            If you select one of these options, you need to certify a money
            purchase pension scheme.
          </div>
          <a
            href={DWP_GUIDANCE_URL}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-[#0A6C74] underline"
          >
            Read guidance on certifying money purchase pension schemes on the
            DWP website
          </a>
        </div>
      </div>

      <div className="mb-[12px] flex items-center justify-between gap-[12px]">
        <div className="text-[15px] font-bold text-[#101828]">
          Pensionable earnings definition
        </div>
        {!!value && !disabled && !!onClear && (
          <AppButton variant="outlined" name="Clear" onClick={onClear} />
        )}
      </div>

      <Radio.Group
        className="w-full"
        value={value || undefined}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      >
        <div className="flex flex-col gap-[20px]">
          {sets.map((set) => (
            <Radio
              key={set.value}
              value={set.value}
              className="!items-start [&>.ant-radio]:!self-start [&>.ant-radio]:mt-[3px] [&_.ant-radio-inner]:!h-[18px] [&_.ant-radio-inner]:!w-[18px] [&_.ant-radio-inner]:!border-2 [&_.ant-radio-inner]:!border-[#101828] [&_.ant-radio-checked_.ant-radio-inner]:!border-[#00795B]"
              style={{ alignItems: 'flex-start' }}
            >
              <div className="mb-[4px] text-[14px] font-semibold text-[#101828]">
                {set.title}
              </div>
              <ul className="m-0 flex list-disc flex-col gap-[6px] pl-[18px]">
                {set.bullets.map((bullet, index) => (
                  <li
                    key={index}
                    className="text-[13px] leading-[1.5] text-[#475467]"
                  >
                    {bullet}
                  </li>
                ))}
              </ul>
            </Radio>
          ))}
        </div>
      </Radio.Group>
    </div>
  );
};

export default SelfCertificationPanel;
